/**
 * CAOSLAB.DEV — MAIN APPLICATION ENTRY
 * "Transformamos el caos digital en crecimiento"
 */

import { initChaosCanvas } from './canvas-chaos.js';
import { initBentoEffects } from './bento.js';
import { initContactModule } from './contact.js';
import { BLOG_POSTS } from './blog.js';

document.addEventListener('DOMContentLoaded', () => {
  // Initialize Core Systems
  initChaosCanvas();
  initBentoEffects();
  initContactModule();
  initFaqAccordion();
  initWebMCP();

  // WebMCP Integration (Browser-Level Agent Tool Discovery)
  function initWebMCP() {
    if (typeof navigator !== 'undefined' && navigator.modelContext && typeof navigator.modelContext.provideContext === 'function') {
      try {
        navigator.modelContext.provideContext({
          tools: [
            {
              name: "getCompanyInfo",
              description: "Obtiene información corporativa oficial de CaosLab (NAP, servicios y canales de contacto).",
              inputSchema: { type: "object", properties: {} },
              execute: async () => ({
                name: "CaosLab",
                domain: "caoslab.dev",
                slogan: "Transformamos el caos digital en crecimiento.",
                phone: "+52 627 889 3344",
                whatsapp: "https://wa.me/526278893344",
                services: [
                  "Desarrollo Web",
                  "SEO (Search Engine Optimization)",
                  "GEO (Generative Engine Optimization)",
                  "Apps Nativas (iOS/Android)",
                  "Agentes de Inteligencia Artificial"
                ],
                clients: [
                  "multiserviciosexpressparral.com",
                  "asesoriasem.com",
                  "omhservicesllc.com"
                ]
              })
            },
            {
              name: "getServices",
              description: "Lista el catálogo detallado de servicios y capacidades tecnológicas de CaosLab.",
              inputSchema: { type: "object", properties: {} },
              execute: async () => ([
                { id: "web", name: "Desarrollo Web", description: "Plataformas web ultrarrápidas <50ms de latencia, WebGL y rendimiento 100/100." },
                { id: "geo", name: "GEO (Generative Engine Optimization)", description: "Optimización ontológica y datos estructurados para citabilidad en ChatGPT, Claude, Perplexity y Gemini." },
                { id: "ai-agents", name: "Agentes de Inteligencia Artificial", description: "Agentes autónomos B2B con tool calling, memoria persistente y automatización." },
                { id: "apps", name: "Apps Nativas", description: "Desarrollo móvil nativo para iOS y Android." },
                { id: "seo", name: "SEO Semántico", description: "Posicionamiento orgánico de alta autoridad." }
              ])
            },
            {
              name: "submitProjectInquiry",
              description: "Envía una solicitud de cotización o proyecto al equipo técnico de CaosLab.",
              inputSchema: {
                type: "object",
                properties: {
                  name: { type: "string", description: "Nombre o empresa" },
                  email: { type: "string", description: "Correo de contacto" },
                  service: { type: "string", description: "Servicio de interés" },
                  message: { type: "string", description: "Descripción del proyecto" }
                },
                required: ["name", "email", "message"]
              },
              execute: async (args) => {
                const nameInput = document.getElementById('form-name');
                const emailInput = document.getElementById('form-email');
                const msgInput = document.getElementById('form-message');
                if (nameInput) nameInput.value = args.name || '';
                if (emailInput) emailInput.value = args.email || '';
                if (msgInput) msgInput.value = args.message || '';
                return {
                  status: "success",
                  message: "Consulta recibida. Redirigiendo a atención inmediata vía WhatsApp (+52 627 889 3344)...",
                  whatsappUrl: `https://wa.me/526278893344?text=${encodeURIComponent(`Hola CaosLab, soy ${args.name || ''}. Mensaje: ${args.message || ''}`)}`
                };
              }
            }
          ]
        });
      } catch (e) {
        console.warn("WebMCP context provision error:", e);
      }
    }
  }

  // FAQ Accordion Interactivity
  function initFaqAccordion() {
    const faqContainer = document.getElementById('faq-accordion-container');
    if (!faqContainer) return;

    const faqItems = faqContainer.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const questionBtn = item.querySelector('.faq-question');
      if (!questionBtn) return;

      questionBtn.addEventListener('click', () => {
        const isCurrentlyActive = item.classList.contains('active');

        // Close all other items in accordion
        faqItems.forEach(otherItem => {
          otherItem.classList.remove('active');
          const otherBtn = otherItem.querySelector('.faq-question');
          if (otherBtn) otherBtn.setAttribute('aria-expanded', 'false');
        });

        // Toggle current item
        if (!isCurrentlyActive) {
          item.classList.add('active');
          questionBtn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  // Render Top 3 Featured Blog Articles Teaser on Homepage
  const blogPreviewGrid = document.getElementById('blog-posts-grid');
  if (blogPreviewGrid) {
    const renderFeatured = (posts) => {
      if (!posts || posts.length === 0) {
        blogPreviewGrid.innerHTML = `
          <div class="empty-blog-state glass-card cyber-corners" style="grid-column: 1 / -1; text-align: center; padding: 60px 24px; border: 1px dashed rgba(212,175,55,0.3); background: rgba(12,12,18,0.7); width: 100%; box-sizing: border-box;">
            <div style="width: 64px; height: 64px; margin: 0 auto 16px auto; border-radius: 50%; background: rgba(212,175,55,0.1); border: 1px solid rgba(212,175,55,0.3); display: flex; align-items: center; justify-content: center;">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#d4af37" stroke-width="1.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </div>
            <span class="admin-badge" style="margin-bottom: 10px;">PRÓXIMAMENTE</span>
            <h3 style="font-size: 1.35rem; color: #ffffff; margin-bottom: 10px; font-family: var(--font-heading);">Artículos Técnicos en Preparación</h3>
            <p style="color: var(--text-muted); max-width: 520px; margin: 0 auto 20px auto; font-size: 0.92rem; line-height: 1.6;">
              Estamos redactando nuestras próximas publicaciones sobre GEO, Inteligencia Artificial y Arquitectura Web. Estarán disponibles el próximo mes.
            </p>
            <a href="#contacto" class="btn btn-gold shimmer-element" style="display: inline-flex;">
              Iniciar Proyecto con CaosLab
            </a>
          </div>
        `;
        return;
      }

      blogPreviewGrid.innerHTML = posts.slice(0, 3).map(post => {
        const postUrl = post.isFirestore ? `/blog/post/?id=${post.id}` : `/blog/${post.id}/`;
        const artCover = post.coverImageUrl 
          ? `<div style="width: 80px; height: 80px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(212,175,55,0.4);"><img src="${post.coverImageUrl}" alt="Portada" style="width: 100%; height: 100%; object-fit: cover;"></div>`
          : `<div style="width: 80px; height: 80px; border-radius: 50%; border: 2px dashed rgba(212,175,55,0.4); display: flex; align-items: center; justify-content: center;">
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#d4af37" stroke-width="1.5">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            </div>`;

        return `
          <a href="${postUrl}" class="blog-card glass-card cyber-corners" style="text-decoration: none;">
            <div class="blog-card-header">
              <div class="blog-card-art">
                ${artCover}
              </div>
              <span class="blog-category-badge">${post.category}</span>
            </div>
            <div class="blog-card-body">
              <div class="blog-meta">
                <span class="blog-meta-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                  ${post.readTime || '5 min'}
                </span>
                <span class="blog-meta-item">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                  ${post.date}
                </span>
              </div>
              <h3 class="blog-title">${post.title}</h3>
              <p class="blog-excerpt">${post.excerpt}</p>
              <div class="blog-read-more">
                Leer Publicación Completa
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"></polyline></svg>
              </div>
            </div>
          </a>
        `;
      }).join('');
    };

    renderFeatured(BLOG_POSTS);

    // Hydrate with Firestore posts in idle background time to prevent TBT blocking
    const hydratePosts = () => {
      import('./firebase.js').then(async ({ getPublishedPosts }) => {
        try {
          const { posts: firestorePosts } = await getPublishedPosts();
          if (firestorePosts && firestorePosts.length > 0) {
            const formatted = firestorePosts.map(fp => ({
              id: fp.id,
              slug: fp.slug || fp.id,
              category: fp.category || 'Desarrollo Web',
              title: fp.title,
              excerpt: fp.excerpt,
              date: fp.publishedAt?.toDate ? fp.publishedAt.toDate().toLocaleDateString('es-MX', { day: '2-digit', month: 'short' }) : 'Reciente',
              readTime: fp.readTime || '5 min',
              coverImageUrl: fp.coverImageUrl || '',
              isFirestore: true
            }));

            const merged = [...formatted];
            BLOG_POSTS.forEach(sp => {
              if (!merged.some(m => m.id === sp.id || m.slug === sp.id)) {
                merged.push(sp);
              }
            });
            renderFeatured(merged);
          }
        } catch (e) {
          // Fallback silently
        }
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(hydratePosts, { timeout: 3000 });
    } else {
      setTimeout(hydratePosts, 2000);
    }
  }

  // Mobile Navigation Drawer Toggle
  const mobileToggle = document.getElementById('mobile-menu-toggle');
  const mobileDrawer = document.getElementById('mobile-drawer');
  const mobileLinks = document.querySelectorAll('.mobile-nav-link');

  if (mobileToggle && mobileDrawer) {
    mobileToggle.addEventListener('click', () => {
      mobileDrawer.classList.toggle('open');
      const isOpen = mobileDrawer.classList.contains('open');
      mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    });

    mobileLinks.forEach(link => {
      link.addEventListener('click', () => {
        mobileDrawer.classList.remove('open');
      });
    });
  }

  // Active Navbar Spy via Zero-Reflow IntersectionObserver
  const sections = document.querySelectorAll('section[id]');
  const navLinks = document.querySelectorAll('.nav-link');

  if ('IntersectionObserver' in window && sections.length > 0) {
    const navObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const id = entry.target.getAttribute('id');
          navLinks.forEach(link => {
            const href = link.getAttribute('href');
            if (href && href.startsWith('#')) {
              link.classList.toggle('active', href === `#${id}`);
            }
          });
        }
      });
    }, {
      rootMargin: '-30% 0px -60% 0px',
      threshold: 0
    });

    sections.forEach(section => navObserver.observe(section));
  }
});
